import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  serverTimestamp,
  increment
} from 'firebase/firestore';

/**
 * Search for a user by document ID or searchUserName
 * @param {string} searchTerm - The search term (document ID or searchUserName)
 * @returns {Promise<Array>} Array of matching user documents
 */
export const searchUser = async (searchTerm) => {
  if (!searchTerm) return [];
  
  try {
    // 1. Поиск по document ID
    const userDocRef = doc(db, 'user', searchTerm);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      return [{ id: userDocSnap.id, ...userDocSnap.data() }];
    }
    
    // 2. Поиск по username (searchUserName)
    const usernameQuery = query(
      collection(db, 'user'),
      where('searchUserName', '==', searchTerm.toLowerCase())
    );
    const usernameSnapshot = await getDocs(usernameQuery);
    const usernameResults = [];
    
    usernameSnapshot.forEach((doc) => {
      usernameResults.push({ id: doc.id, ...doc.data() });
    });
    
    if (usernameResults.length > 0) {
      return usernameResults;
    }
    
    // 3. Поиск по email (profile.email), если предыдущие варианты не дали результата
    const emailQuery = query(
      collection(db, 'user'),
      where('profile.email', '==', searchTerm.toLowerCase())
    );
    const emailSnapshot = await getDocs(emailQuery);
    const emailResults = [];
    
    emailSnapshot.forEach((doc) => {
      emailResults.push({ id: doc.id, ...doc.data() });
    });
    
    return emailResults;
  } catch (error) {
    console.error('Error searching for user:', error);
    throw error;
  }
};


/**
 * Get detailed information about user photos including subcollections
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data with detailed photo information
 */
export const getUserWithPhotoDetails = async (userId) => {
  try {
    // Get main user document
    const userDocRef = doc(db, 'user', userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (!userDocSnap.exists()) {
      return null;
    }
    
    const userData = { id: userDocSnap.id, ...userDocSnap.data() };
    
    // Get photos subcollection for detailed information
    const photosCollectionRef = collection(db, 'user', userId, 'photos');
    const photosSnap = await getDocs(photosCollectionRef);
    
    const photoDetails = {};
    
    photosSnap.forEach((photoDoc) => {
      photoDetails[photoDoc.id] = photoDoc.data();
    });
    
    // Add vote data to existing photos 
    if (userData.photos && userData.photos.length > 0) {
      userData.photos = userData.photos.map(photo => {
        const details = photoDetails[photo.uid] || {};
        return {
          ...photo,
          numberOfRates: details.numberOfRates || 0,
          summOfRates: details.summOfRates || 0,
          whoVoted: details.whoVoted || []
        };
      });
    }
    
    return userData;
  } catch (error) {
    console.error('Error getting user with photo details:', error);
    throw error;
  }
};

/**
 * Get a user by document ID
 * @param {string} userId - The user document ID
 * @returns {Promise<Object|null>} User document or null if not found
 */
export const getUserById = async (userId) => {
  try {
    const userDocRef = doc(db, 'user', userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      return { id: userDocSnap.id, ...userDocSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by ID:', error);
    throw error;
  }
};

/**
 * Update user subscription
 * @param {string} userId - User ID
 * @param {number} subscriptionType - Subscription type (0 - free, 1-4 - premium, 5 - referral)
 * @param {string} subscriptionData - Additional subscription data
 * @returns {Promise<void>}
 */
export const updateUserSubscription = async (userId, subscriptionType, subscriptionData = "") => {
  try {
    const userDocRef = doc(db, 'user', userId);
    
    await updateDoc(userDocRef, {
      "subscribtion.type": subscriptionType,
      "subscribtion.data": subscriptionData,
      "subscribtion.created": serverTimestamp()
    });
    
    // If subscription type is not 0 (active subscription), add +1000 to rating balance
    if (subscriptionType !== 0) {
      await updateDoc(userDocRef, {
        "rates.balance": increment(1000)
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating user subscription:', error);
    throw error;
  }
};

/**
 * Delete user with all related data
 * @param {string[]} userIds - Array of user IDs to delete
 * @returns {Promise<boolean>} Operation result
 */
export const deleteUsers = async (userIds) => {
  if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
    throw new Error('You must specify an array of user IDs to delete');
  }

  try {
    // Send request to delete users to the backend
    // Using Fetch API instead of direct Firestore access
    const apiUrl = 'https://us-central1-dutiap-project.cloudfunctions.net/apiadmin/dellusersbyid';
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids: userIds })
    });

    if (!response.ok) {
      throw new Error(`Error deleting users: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('Error deleting users:', error);
    throw error;
  }
};

/**
 * Get user profile rating votes from rate-profile subcollection
 * @param {string} userId - The user document ID
 * @returns {Promise<Array>} Array of profile rating votes
 */
export const getUserProfileRatings = async (userId) => {
  try {
    // First try to use Firebase Firestore directly
    try {
      const rateProfileCollectionRef = collection(db, 'user', userId, 'rate-profile');
      const rateProfileSnap = await getDocs(rateProfileCollectionRef);
      
      const profileRatings = [];
      
      rateProfileSnap.forEach((rateDoc) => {
        // Each document in rate-profile has the voted user's ID as the document ID
        // and contains mark and timestamp fields
        profileRatings.push({
          votedUserId: rateDoc.id,
          ...rateDoc.data()
        });
      });
      
      return profileRatings;
    } catch (firestoreError) {
      console.error('Error accessing Firestore directly:', firestoreError);
      
      // Fallback: Use the admin API if direct Firestore access fails
      // This is a safer approach that ensures admin-site remains isolated
      const apiUrl = 'https://us-central1-dutiap-project.cloudfunctions.net/apiadmin/getuserrateprofile';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId })
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching rate-profile data: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.ratings || [];
    }
  } catch (error) {
    console.error('Error getting user profile ratings:', error);
    return []; // Return empty array instead of throwing to prevent UI errors
  }
};

/**
 * Get user referrals from referral subcollection
 * @param {string} userId - The user document ID
 * @returns {Promise<Object>} Object with referrals array and metadata
 */
export const getUserReferrals = async (userId) => {
  try {
    // Always use the admin API endpoint for referrals to ensure proper access control
    const apiUrl = 'https://us-central1-dutiap-project.cloudfunctions.net/apiadmin/getuserreferrals';
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId })
    });
    
    if (!response.ok) {
      throw new Error(`Error fetching referrals data: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      referrals: data.referrals || [],
      hasReferrals: data.hasReferrals || false,
      totalCount: data.totalCount || 0
    };
  } catch (error) {
    console.error('Error getting user referrals:', error);
    return { 
      referrals: [],
      hasReferrals: false,
      totalCount: 0
    };
  }
};

/**
 * Get users by username for deletion
 * @param {string[]} usernames - Array of user usernames
 * @returns {Promise<string[]>} Array of user IDs
 */
export const getUsersForDelete = async (usernames) => {
  if (!usernames || !Array.isArray(usernames) || usernames.length === 0) {
    throw new Error('You must specify an array of user usernames');
  }

  try {
    // Send request to get user IDs by username
    const apiUrl = 'https://us-central1-dutiap-project.cloudfunctions.net/apiadmin/getuserfordelete';
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userArray: usernames })
    });

    if (!response.ok) {
      throw new Error(`Error getting user IDs: ${response.statusText}`);
    }

    const userIds = await response.json();
    return userIds;
  } catch (error) {
    console.error('Error getting users for delete:', error);
    throw error;
  }
};